import { type CalendarZoom } from "@/features/calendar/types";
import styled from "@emotion/styled";
import { LightMode, Text, Variables } from "@repo/skapa";
import { padZeros } from "@repo/utils";

import { getZoomHourWidth } from "../utils/utils";
import { NAME_COL_WIDTH } from "./constants";

export const CalendarRow = styled.div`
  position: relative;
  display: flex;
`;

const StickyCalendarRow = styled(CalendarRow)`
  position: sticky;
  top: 0;
  z-index: 4;
  background-color: ${LightMode.colourStaticWhite};
`;

export const HourBlock = styled.div<{
  width: number;
  first?: boolean;
  last?: boolean;
  noLeftBorder?: boolean;
  color?: string;
  onClick?: () => void;
}>`
  width: ${({ width }) => width}px;

  ${({ last }) =>
    last
      ? ""
      : `border-bottom: ${Variables.thicknessThin} solid ${LightMode.colourNeutral4};`};
  ${({ first, noLeftBorder }) =>
    first || noLeftBorder
      ? ""
      : `border-left: ${Variables.thicknessThin} solid ${LightMode.colourNeutral4};`};

  background-color: ${({ color }) => color ?? LightMode.colourStaticWhite};

  ${({ onClick }) =>
    onClick
      ? `
      cursor: pointer;
      &:hover {
        border: ${Variables.thicknessThin} dashed ${LightMode.colourInteractiveEmphasisedBgDefault};
        background: ${LightMode.colourStaticWhite};
        margin-top: -1px;
      }`
      : ""};
`;

export const NotWorkingHourBlock = styled.div<{
  width: number;
  first?: boolean;
  last?: boolean;
  noLeftBorder?: boolean;
  onClick?: () => void;
}>`
  width: ${({ width }) => width}px;
  ${({ last }) =>
    last
      ? ""
      : `border-bottom: ${Variables.thicknessThin} dashed ${LightMode.colourNeutral4};`};
  ${({ first, noLeftBorder }) =>
    first || noLeftBorder
      ? ""
      : `border-left: ${Variables.thicknessThin} dashed ${LightMode.colourNeutral4};`};

  ${({ onClick }) =>
    onClick
      ? `
      cursor: pointer;
      &:hover {
        border: ${Variables.thicknessThin} dashed ${LightMode.colourInteractiveEmphasisedBgDefault};
        background: rgb(var(--colour-interactive-emphasised-bg-default, 0, 88, 163), 0.05);
        margin-top: -1px;
      }`
      : ""};
`;

const HourTitleBlock = styled(HourBlock)<{
  leftMargin?: boolean;
  sticky?: boolean;
}>`
  ${({ sticky }) => (sticky ? "position: sticky" : "")};
  ${({ sticky }) => (sticky ? "left: 0" : "")};
  ${({ sticky }) => (sticky ? "z-index: 3" : "")};

  ${({ first, noLeftBorder }) =>
    first || noLeftBorder
      ? ""
      : `border-left: ${Variables.thicknessThin} solid ${LightMode.colourStaticWhite};`};
`;

const HeaderNameColumn = styled.div`
  width: ${NAME_COL_WIDTH}px;
  position: sticky;
  left: 0;
  z-index: 5;
  background-color: ${LightMode.colourStaticWhite};
`;

const MarginBlock = styled.div<{
  first?: boolean;
}>`
  ${({ first }) => (first ? "" : "margin-left: -18px;")};
`;

export const HourTitles = ({
  zoom,
  startHour,
  totalHours,
}: {
  startHour: number;
  totalHours: number;
  zoom: {
    zoom: CalendarZoom;
    zoomWidth: number;
  };
}) => {
  const blocks = [];
  let hour = startHour;
  for (let i = 1; i <= totalHours; i++) {
    blocks.push(
      <HourTitleBlock
        key={i}
        width={getZoomHourWidth(zoom, totalHours)}
        leftMargin
        last
        noLeftBorder={i === 1}
      >
        <MarginBlock first={i === 1}>
          <Text textStyle="Body.M">{`${padZeros(hour)}:00`}</Text>
        </MarginBlock>
      </HourTitleBlock>
    );
    hour++;
  }
  return (
    <StickyCalendarRow>
      <HeaderNameColumn />
      {blocks}
    </StickyCalendarRow>
  );
};
